import React, { useEffect, useState } from 'react';
import { useApolloClient, useLazyQuery } from '@apollo/react-hooks';
import { GET_INVITE_CODE_INFO_TAB } from './queries';
import UIIconButton from '../../../../../../components/UI/UIIconButton';
import UIModal from '../../../../../../components/UI/UIModal';
import Queries from '../../../../../../utils/API/Queries';
import { toast } from 'react-toastify';
import UIListInputRow from '../../../../../../components/UI/UIListInputRow';
import { UI_LABELED_INPUT_STATIC_TYPE, UI_LABELED_INPUT_TEXT_TYPE } from '../../../../../../components/UI/UILabeledInput/config';
import UIHover from '../../../../../../components/UI/UIHover/UIHover';
import { PlusIcon } from '../../../../../../assets/icons';
import { useSelector } from 'react-redux';
import { getIsUserAdminSelector, getSignedInUserSelector } from '../../../../../../redux/selectors/CurrentUserSelectors';
import Promo from '../../../../../../components/UI/UIPromo/UIPromo';
import CreatePromoModal from '../../../../../../components/modals/CreatePromoModal';
import * as strings from './strings';
import { UI_ICON_BUTTON_CANCEL_TYPE, UI_ICON_BUTTON_FILL_TYPE } from '../../../../../../components/UI/UIIconButton/config';

export default function InviteCodeInfoTab({ inviteId }: InviteCodeInfoTabProps) {
	let isUserAdmin = useSelector(getIsUserAdminSelector);
	let selectedUser = useSelector(getSignedInUserSelector);
	const client = useApolloClient();
	const [inviteCode, setInviteCode] = useState<InviteCodeType>();
	const [inviteCodeValue, setInviteCodeValue] = useState<string>('');
	const [isSaving, setIsSaving] = useState<boolean>(false);
	const [showCreatePromotionModal, setShowCreatePromotionModal] = useState<boolean>(false);

	const [getInviteCode, { data: inviteCodeData }] = useLazyQuery(GET_INVITE_CODE_INFO_TAB, {
		variables: { q: { id: { op_eq: inviteId } } }
	});

	useEffect(() => {
		(async () => {
			await getInviteCode();
		})();
	}, []);

	useEffect(() => {
		(async () => {
			if (inviteCodeData) {
				setInviteCode(inviteCodeData.forms[0]);
				setInviteCodeValue(inviteCodeData.forms[0].inviteCode);
			}
		})();
	}, [inviteCodeData]);

	if (!inviteCode) return null;

	const onChangeInviteCode = async () => {
		if (!isSaving) {
			setIsSaving(true);
			const response = await client
				.mutate({
					mutation: Queries.UPDATE_FORM,
					variables: { id: inviteCode.id, inviteCode: inviteCodeValue }
				})
				.catch((error) => {
					toast.error(error.message);
				});
			const { updateForm } = response['data'];
			setIsSaving(false);

			if (updateForm) {
				toast.error(strings.errorChangingName);
			} else {
				toast.success(strings.successChangingName);
				setInviteCode(updateForm);
			}
		}
	};

	const renderPromos = () => {
		const removePromo = async (promo) => {
			if (inviteCode.formData) {
				inviteCode.formData.promos = inviteCode.formData.promos.filter((formPromo) => formPromo['id'] !== promo.id);
				const response = await client
					.mutate({
						mutation: Queries.UPDATE_FORM,
						variables: { id: inviteCode.id, formData: inviteCode.formData }
					})
					.catch((error) => {
						toast.error(error.message);
					});
				const { updateForm } = response['data'];
				if (!updateForm) {
					toast.error(strings.errorRemovingPromo);
				} else {
					toast.success(strings.successRemovingPromo);
				}
				await getInviteCode();
			}
		};

		if (inviteCode.formData) {
			let promos = inviteCode.formData.promos;
			if (promos.length > 0) {
				return inviteCode.formData.promos.map((promo, index) => {
					const { discountAmount } = promo;
					return <Promo key={`${discountAmount}-${index}`} removePromo={removePromo} updateInviteCode={getInviteCode} promo={promo} />;
				});
			}
		}
		return <p>There are no promos...</p>;
	};

	const toggleActive = async () => {
		const response = await client.mutate({
			mutation: Queries.UPDATE_FORM,
			variables: { id: inviteCode.id, isActive: !inviteCode.isActive }
		});
		const { updateForm } = response['data'];
		if (updateForm) {
			toast.success(inviteCode.isActive ? strings.successDeactivated : strings.successReactivated);
			await getInviteCode();
		}
	};

	const renderCreatePromotionModal = () => {
		const hideModal = () => setShowCreatePromotionModal(false);
		return (
			<UIModal condition={showCreatePromotionModal}>
				<CreatePromoModal
					userId={selectedUser.id}
					hideModal={hideModal}
					inviteCode={inviteCode}
					onFinish={async () => {
						await getInviteCode();
						hideModal();
					}}
				/>
			</UIModal>
		);
	};

	if (!inviteCode) return null;

	return (
		<div className='inviteCode'>
			<div className='inviteCode__card'>
				<div className='inviteCode__card--code-input'>
					<UIListInputRow
						style={{ marginRight: '8px' }}
						label={strings.inviteCodeLabel}
						type={isUserAdmin ? UI_LABELED_INPUT_TEXT_TYPE : UI_LABELED_INPUT_STATIC_TYPE}
						containerStyle={{ width: '100%' }}
						isLoading={isSaving}
						savedValue={inviteCode.inviteCode}
						onSave={onChangeInviteCode}
						onChangeValue={(inviteCodeValue) => setInviteCodeValue(inviteCodeValue)}
						value={inviteCodeValue}
						placeholder={strings.inviteCodePlaceholder}
					/>
				</div>
				<div>
					{isUserAdmin && (
						<UIIconButton
							key={inviteCode.id}
							dark
							style={{ marginTop: 16 }}
							onClick={toggleActive}
							text={inviteCode.isActive ? strings.buttonDeactivated : strings.buttonReactivated}
							type={inviteCode.isActive ? UI_ICON_BUTTON_CANCEL_TYPE : UI_ICON_BUTTON_FILL_TYPE}
						/>
					)}
				</div>
				<div className='inviteCode__card--promotions'>
					<p className='inviteCode__card--promotions-text'>{strings.promotionsTitle}</p>
					<UIHover text={strings.addPromoHover}>
						<img className='plusIcon' src={PlusIcon.mint} alt={strings.addPromoButtonAlt} onClick={() => setShowCreatePromotionModal(true)} />
					</UIHover>
				</div>
				{renderCreatePromotionModal()}
				{renderPromos()}
			</div>
			<div className='flex-1-1-100' />
		</div>
	);
}
